// The one styled "are you sure?" dialog (2026-10-27, docs/contact-view.md
// "Confirm prompts"), in place of the browser's confirm box: the same MUI
// Dialog look as "Park this contact" and "Log out?". A title, a short body
// (and an optional second line), and two buttons that say what they do. The
// left one (cancelLabel) is the default: focused, and what Escape or a click
// outside means. The wording lives in confirmPrompts.js.
//
// useConfirmDialog() gives a promise-based ask():
//   const [ask, dialog, dismiss] = useConfirmDialog()
//   if (!(await ask(SOME_PROMPT))) return
//   ... and render {dialog} once.
// A second ask while one is open answers the first "no"; dismiss() answers
// an open one "no" (a conversation switch); so does unmounting.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'

// altLabel (2026-10-30, the untold-customer reminder): an optional third
// answer between the two, resolving 'alt'. cancelLabel null hides the cancel
// button; Escape and a click outside still answer "no", and the alt button
// takes the focus.
export default function ConfirmDialog({ open, title, body, extra = null, cancelLabel = 'Cancel', altLabel = null, confirmLabel = 'Continue', confirmColor = 'primary', onCancel, onAlt = null, onConfirm, testId = 'confirm-dialog' }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth data-testid={testId} aria-labelledby={`${testId}-title`}>
      <DialogTitle id={`${testId}-title`}>{title}</DialogTitle>
      <DialogContent>
        {body && <Typography>{body}</Typography>}
        {extra && <Typography sx={{ mt: 1, fontWeight: 600 }} data-testid={`${testId}-extra`}>{extra}</Typography>}
      </DialogContent>
      <DialogActions>
        {cancelLabel !== null && <Button onClick={onCancel} autoFocus data-testid={`${testId}-cancel`}>{cancelLabel}</Button>}
        {altLabel && <Button variant="outlined" onClick={onAlt} autoFocus={cancelLabel === null} data-testid={`${testId}-alt`}>{altLabel}</Button>}
        <Button variant="contained" color={confirmColor} onClick={onConfirm} data-testid={`${testId}-confirm`}>{confirmLabel}</Button>
      </DialogActions>
    </Dialog>
  )
}

export function useConfirmDialog() {
  const [pending, setPending] = useState(null)
  const pendingRef = useRef(null)
  // The last prompt shown, so the text stays while the dialog fades out.
  const [shown, setShown] = useState(null)

  const answer = useCallback((value) => {
    const p = pendingRef.current
    pendingRef.current = null
    setPending(null)
    p?.resolve(value)
  }, [])

  const ask = useCallback((prompt) => new Promise((resolve) => {
    pendingRef.current?.resolve(false)
    const p = { prompt, resolve }
    pendingRef.current = p
    setPending(p)
    setShown(prompt)
  }), [])

  const dismiss = useCallback(() => { if (pendingRef.current) answer(false) }, [answer])

  useEffect(() => () => { pendingRef.current?.resolve(false); pendingRef.current = null }, [])

  const dialog = (
    <ConfirmDialog
      {...(shown ?? {})}
      open={pending != null}
      onCancel={() => answer(false)}
      onAlt={() => answer('alt')}
      onConfirm={() => answer(true)}
    />
  )
  return [ask, dialog, dismiss]
}
